import type { FC } from 'react';
import React from 'react';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { EditorBreadcrumbs } from '@confluence/editor-breadcrumbs';
import { EditorAppearanceButtonNext } from '@confluence/editor-appearance-button';
import { LivePagesByLineArea } from '@confluence/content-header/entry-points/LivePagesByLineArea';
import { TemplateSuggestions } from '@confluence/template-suggestions';
import {
	useContentComponentsContext,
	TitleAreaContentComponents,
} from '@confluence/title-area-content-components';
import { usePrimaryToolbarContext } from '@confluence/editor-primary-toolbar-components';
import type { ContentTypeType } from '@confluence/editor-features/entry-points/types';
import { useSpaceType } from '@confluence/create-space/entry-points/useSpaceType';
import type { ContentClassificationMetadata } from '@confluence/data-classification';
import { useTransitionId } from '@confluence/route-manager';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';

export type TitleAreaContentComponentsWrapperProps = {
	contentId: string;
	linkCreation: boolean;
	focusEditor: () => void;
	onFocusChanged: (focusChanged: boolean) => void;
	debounceOnChange: boolean;
	userOffline: boolean;
	templateSuggestionsRef?: React.RefObject<HTMLDivElement> | null;
	templateButtonRef?: React.RefObject<HTMLElement> | null;
	hideSpinner: () => void;
	viewMode?: boolean;
	contentClassificationMetadata?: ContentClassificationMetadata;
	version?: number;
	isEditorEmpty?: boolean;
	editorApi?: FullPageEditorPresetAPI;
};

export const TitleAreaContentComponentsWrapper: FC<TitleAreaContentComponentsWrapperProps> = ({
	contentId,
	linkCreation,
	focusEditor,
	onFocusChanged,
	debounceOnChange,
	userOffline,
	templateSuggestionsRef,
	templateButtonRef,
	hideSpinner,
	viewMode,
	contentClassificationMetadata,
	version,
	isEditorEmpty,
	editorApi,
}) => {
	const isLivePage = useIsCurrentPageLive();
	const {
		collabEditState,
		editorActions,
		flags,
		contentType,
		shouldRenderPreview,
		isPublishingContent,
		isUnpublishedDraft,
		spaceKey,
	} = usePrimaryToolbarContext();

	const {
		contentComponentsTitle,
		contentComponentsDisabled,
		handleTitleChange,
		breadcrumbsClickHandler,
		beforeUpdateRequest,
		postUpdateRequest,
	} = useContentComponentsContext();

	const { isKnowledgeBaseSpace } = useSpaceType({ spaceKey });

	const transitionId = useTransitionId();
	const isInitialLoad = !transitionId;
	const ssrOrInitial = !!process.env.REACT_SSR || (isInitialLoad && window.__SSR_RENDERED__);

	const isTitleDisabled = !ssrOrInitial && (contentComponentsDisabled || linkCreation);

	const EditorAppearanceButton = (
		<EditorAppearanceButtonNext
			contentId={contentId}
			disabled={contentComponentsDisabled}
			isStatusDisconnected={collabEditState.collabEditInternetLost}
		/>
	);

	const breadcrumbsComponent = (
		<EditorBreadcrumbs
			key="breadcrumbs-line"
			contentId={contentId}
			contentType={contentType as ContentTypeType}
			EditContentAppearanceButton={EditorAppearanceButton}
			breadcrumbsClickHandler={breadcrumbsClickHandler}
			isPublishingContent={isPublishingContent}
			userOffline={userOffline}
			collabEditInitialized={collabEditState.collabEditInitialized}
			moveLocationConfig={{
				spaceKey,
				isUnpublishedDraft,
			}}
			isKnowledgeBaseSpace={isKnowledgeBaseSpace}
		/>
	);

	const livePagesByLineComponent = (
		<LivePagesByLineArea
			contentId={contentId}
			contentType={contentType}
			isPublishingContent={isPublishingContent}
			userOffline={userOffline}
			editorAppearanceButton={EditorAppearanceButton}
			isLivePage={isLivePage}
			isKnowledgeBaseSpace={isKnowledgeBaseSpace}
			viewMode={viewMode}
			editorApi={editorApi}
		/>
	);

	const templateSuggestionsComponent = (
		<TemplateSuggestions
			title={contentComponentsTitle}
			spaceKey={spaceKey}
			contentId={contentId}
			isUnpublishedDraft={isUnpublishedDraft}
			editorActions={editorActions}
			flags={flags}
			userOffline={userOffline}
			hideSpinner={hideSpinner}
			beforeUpdateRequest={beforeUpdateRequest}
			postUpdateRequest={postUpdateRequest}
			templateSuggestionsRef={templateSuggestionsRef}
			templateButtonRef={templateButtonRef}
		/>
	);

	return (
		<TitleAreaContentComponents
			key="title-area-content-components"
			title={contentComponentsTitle}
			breadcrumbsComponent={breadcrumbsComponent}
			templateSuggestionsComponent={templateSuggestionsComponent}
			contentId={contentId}
			isDisabled={isTitleDisabled}
			onChange={handleTitleChange}
			focusEditor={focusEditor}
			onFocusChanged={onFocusChanged}
			debounceOnChange={debounceOnChange}
			contentType={contentType as ContentTypeType}
			userOffline={userOffline}
			isInPreview={shouldRenderPreview}
			isPublishingContent={isPublishingContent}
			isUnpublishedDraft={isUnpublishedDraft}
			templateSuggestionsRef={templateSuggestionsRef}
			templateButtonRef={templateButtonRef}
			contentTopperComponent={livePagesByLineComponent}
			linkCreation={linkCreation}
			viewMode={viewMode}
			contentClassificationMetadata={contentClassificationMetadata}
			version={version}
			isEditorEmpty={isEditorEmpty}
			contentComponentsDisabled={contentComponentsDisabled}
			isStatusDisconnected={collabEditState.collabEditInternetLost}
			editorApi={editorApi}
		/>
	);
};
